import { useCallback, useEffect, useRef, useState } from 'react';

import { redactGraphqlRequestErrorForTelemetry } from 'components/api/graphqlRequestError';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { captureError } from 'helpers/captureError';

import { HubBuyerTourCancelDocument } from './__generated__/hub-tour-cancel.zeus';
import type { HubTour } from './useHomeTours';

const TOUR_CANCEL_OPERATION = 'HubBuyerTourCancel';

export function useTourCancel(): {
  canceledTourIds: ReadonlySet<string>;
  cancel: (tour: HubTour) => Promise<boolean>;
} {
  // The hub snapshot is loaded once per visit and Rails omits canceled tours, so canceled
  // rows are tracked here until the next load drops them.
  const [canceledTourIds, setCanceledTourIds] = useState<ReadonlySet<string>>(() => new Set());
  const controllersByTourId = useRef(new Map<string, AbortController>());

  useEffect(function abortPendingCancelsOnUnmount() {
    const controllers = controllersByTourId.current;
    return function abortPendingCancels() {
      for (const controller of controllers.values()) {
        controller.abort();
      }
      controllers.clear();
    };
  }, []);

  const cancel = useCallback(async function cancel(tour: HubTour) {
    if (controllersByTourId.current.has(tour.id)) {
      return false;
    }

    const controller = new AbortController();
    controllersByTourId.current.set(tour.id, controller);
    try {
      const response = await requestZeusFromBrowser(
        HubBuyerTourCancelDocument,
        { input: { id: tour.id } },
        controller.signal,
      );
      if (controller.signal.aborted) {
        return false;
      }

      const result = response.buyerTourCancel;
      // Zeus returns UnauthorizedError when the tour is no longer among the viewer's active
      // appointments, which for a tour this page listed means it was already canceled elsewhere.
      const isCanceled =
        (result.__typename === 'CommandResult' && result.success) ||
        result.__typename === 'UnauthorizedError';
      if (!isCanceled) {
        captureError(new Error(`Tour cancel returned ${result.__typename}`), TOUR_CANCEL_OPERATION);
        return false;
      }

      setCanceledTourIds((current) => new Set(current).add(tour.id));
      return true;
    } catch (error) {
      if (controller.signal.aborted) {
        return false;
      }

      captureError(redactGraphqlRequestErrorForTelemetry(error), TOUR_CANCEL_OPERATION);
      return false;
    } finally {
      if (controllersByTourId.current.get(tour.id) === controller) {
        controllersByTourId.current.delete(tour.id);
      }
    }
  }, []);

  return { canceledTourIds, cancel };
}
